import React, { useEffect, useState } from "react";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaCheckCircle,
  FaGlobeAsia,
} from "react-icons/fa";
import { MdLocalHospital, MdSchool } from "react-icons/md";
import api from "../services/api";
import { ENDPOINTS } from "../services/endpoints";

interface HospitalItem {
  id?: number | string;
  hospitals_code?: string;
  hospitals_name?: string;
  hospital_category?: string;
  collaborating_hospital?: string;
  address?: string;
  city?: string;
  state?: string;
  postcode?: string;
}

const initiatives = [
  {
    id: "wia",
    icon: MdLocalHospital,
    title: "Cancer Institute (WIA)",
    description:
      "The Cancer Institute (WIA) provides clinical, epidemiological, and registry expertise for PBCCR. Its long-standing cancer registry experience supports standardised childhood cancer data collection, validation, analysis, and reporting.",
    points: [
      "Technical coordination for population-based childhood cancer registry activities",
      "Training and guidance for registry staff and healthcare professionals",
      "Support for data standards, validation, analysis, and reporting",
      "Integration with the Tamil Nadu Cancer Registry Program for statewide surveillance",
    ],
  },
  {
    id: "dme",
    icon: MdSchool,
    title: "Directorate of Medical Education (DME)",
    description:
      "The Directorate of Medical Education supports engagement with teaching hospitals and medical institutions that contribute to childhood cancer diagnosis, treatment, and registry reporting.",
    points: [
      "Enables collaboration with medical colleges and tertiary care centers",
      "Supports institutional participation in childhood cancer data reporting",
      "Strengthens clinical pathways for registry-based review and follow-up",
      "Encourages standardized reporting across participating teaching hospitals",
    ],
  },
];

const Initiatives = () => {
  const [activeId, setActiveId] = useState(initiatives[0].id);
  const [hospitals, setHospitals] = useState<HospitalItem[]>([]);
  const [activeHospitalIndex, setActiveHospitalIndex] = useState(0);

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const response = await api.get(ENDPOINTS.HOSPITALS.COLLABORATORS_LIST);
        const hospitalList = response?.data?.hospital_list ?? response?.data?.data ?? [];
        const normalizedHospitals = Array.isArray(hospitalList) ? hospitalList : [];
        setHospitals(
          normalizedHospitals.filter(
            (hospital) => hospital.collaborating_hospital === "Yes",
          ),
        );
      } catch (error) {
        console.error("Failed to fetch hospital collaborators:", error);
        setHospitals([]);
      }
    };

    fetchHospitals();
  }, []);

  const toggleInitiative = (id: string) => {
    setActiveId((currentId) => (currentId === id ? "" : id));
  };

  const visibleCount = 6;
  const maxHospitalIndex = Math.max(hospitals.length - visibleCount, 0);
  const visibleHospitals = hospitals.slice(
    activeHospitalIndex,
    activeHospitalIndex + visibleCount,
  );

  const showPreviousHospitals = () => {
    setActiveHospitalIndex((current) =>
      current === 0 ? maxHospitalIndex : current - 1,
    );
  };

  const showNextHospitals = () => {
    setActiveHospitalIndex((current) =>
      current === maxHospitalIndex ? 0 : current + 1,
    );
  };

  return (
    <div className="initiatives-page">
      <section className="initiatives-shell">
        <div className="initiatives-heading">
          <h1>Our Initiatives</h1>
          <p>
            Key organizations supporting cancer data collection and healthcare
            improvement across the region.
          </p>
        </div>

        <div className="initiatives-accordion">
          {initiatives.map((initiative) => {
            const isOpen = activeId === initiative.id;
            return (
              <article
                className={`initiatives-item${isOpen ? " is-open" : ""}`}
                key={initiative.id}
              >
                <button
                  type="button"
                  className="initiatives-trigger"
                  onClick={() => toggleInitiative(initiative.id)}
                  aria-expanded={isOpen}
                  aria-controls={`${initiative.id}-panel`}
                >
                  <span className="initiatives-icon" aria-hidden="true">
                    {React.createElement(initiative.icon as any)}
                  </span>
                  <span className="initiatives-trigger-title">
                    {initiative.title}
                  </span>
                  <span className="initiatives-chevron" aria-hidden="true">
                    {isOpen
                      ? React.createElement(FaChevronUp as any)
                      : React.createElement(FaChevronDown as any)}
                  </span>
                </button>

                {isOpen && (
                  <div
                    className="initiatives-panel"
                    id={`${initiative.id}-panel`}
                  >
                    <div className="initiatives-copy">
                      <h2>{initiative.title}</h2>
                      <p>{initiative.description}</p>
                      <ul>
                        {initiative.points.map((point) => (
                          <li key={point}>
                            {React.createElement(FaCheckCircle as any, {
                              "aria-hidden": true,
                            })}
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <section className="initiatives-hospital-section">
          <h2>Collaborating Hospitals</h2>

          {hospitals.length > 0 ? (
            <div className="initiatives-hospital-carousel">
              <button
                type="button"
                className="about-carousel-control initiatives-carousel-control-left"
                onClick={showPreviousHospitals}
                aria-label="Show previous hospital cards"
                disabled={hospitals.length <= visibleCount}
              >
                {React.createElement(FaChevronLeft as any, { "aria-hidden": true })}
              </button>

              <div className="initiatives-hospital-grid">
                {visibleHospitals.map((hospital, index) => {
                  const hospitalName =
                    hospital.hospitals_name || `Hospital ${index + 1}`;
                  const cityState = [hospital.city, hospital.state]
                    .filter(Boolean)
                    .join(", ");
                  const address = hospital.address || cityState || "Tamil Nadu";

                  return (
                    <article
                      className="initiatives-hospital-card"
                      key={
                        hospital.id ??
                        hospital.hospitals_code ??
                        `${hospitalName}-${index}`
                      }
                    >
                      <div className="about-member-tags initiatives-hospital-tags">
                        <span>{hospital.hospital_category || "Hospital"}</span>
                      </div>
                      <h3>{hospitalName}</h3>
                      <p>{address}</p>
                    </article>
                  );
                })}
              </div>

              <button
                type="button"
                className="about-carousel-control initiatives-carousel-control-right"
                onClick={showNextHospitals}
                aria-label="Show next hospital cards"
                disabled={hospitals.length <= visibleCount}
              >
                {React.createElement(FaChevronRight as any, { "aria-hidden": true })}
              </button>
            </div>
          ) : (
            <p className="initiatives-loading-text">
              Loading participating hospitals...
            </p>
          )}
        </section>
      </section>
    </div>
  );
};

export default Initiatives;
